/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import filtersSchema from './filtersSchema';
import SessionEditor from './FiltersSessionEditor';

const FiltersForm = ({ value, onChange }) => {
  const { register, handleSubmit, formState: { errors }, watch, reset, setValue } = useForm({
    resolver: zodResolver(filtersSchema),
    defaultValues: value,
  });

  const onSubmit = (data) => {
    onChange(normalizeSessionsOut(data));
  };

  useEffect(() => {
    // Sync external changes
    reset(value);
  }, [value, reset]);

  useEffect(() => {
    const subscription = watch((f) => {
      onChange(normalizeSessionsOut(f));
    });
    return () => subscription.unsubscribe();
  }, [watch, onChange]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <h3 className="text-lg font-semibold mb-4">Filters</h3>
      <div className="space-y-4">
        <div>
          <label>Allowed Days</label>
          <div className="flex flex-wrap gap-4">
            <label><input type="checkbox" {...register('days.mon')} /> Mon</label>
            <label><input type="checkbox" {...register('days.tue')} /> Tue</label>
            <label><input type="checkbox" {...register('days.wed')} /> Wed</label>
            <label><input type="checkbox" {...register('days.thu')} /> Thu</label>
            <label><input type="checkbox" {...register('days.fri')} /> Fri</label>
            <label><input type="checkbox" {...register('days.sat')} /> Sat</label>
            <label><input type="checkbox" {...register('days.sun')} /> Sun</label>
          </div>
        </div>
        <div>
          <label className="block mb-1">Sessions (UTC)</label>
          <p className="text-[11px] text-textSecondary mb-2">Overnights supported (e.g., 22:00-02:00). 24:00 is midnight.</p>
          {/* For now, show raw JSON view to allow adding multiple windows. Could be upgraded to a structured editor. */}
          <Input type="text" {...register('session')} className="hidden" />
          <SessionEditor controlValue={watch('session') || []} setSession={(next)=>setValue('session', next, { shouldDirty: true, shouldTouch: true, shouldValidate: true })} />
        </div>
      </div>
    </form>
  );
};

function normalizeSessionsOut(data) {
  const toBackendTime = (t) => t === '23:59:59' ? '24:00' : t; // map display midnight back to BE's accepted 24:00
  const session = Array.isArray(data?.session)
    ? data.session.map((w) => Array.isArray(w)
        ? { start: toBackendTime(w[0]), end: toBackendTime(w[1]) }
        : { start: toBackendTime(w?.start), end: toBackendTime(w?.end) })
    : [];
  return { ...data, session };
}

export default FiltersForm;
